// Private-property region recommendations. There is no per-unit private
// transaction open data (that needs the URA key), and scraping listing sites is
// off-limits — so we recommend at the region/district level: which of CCR / RCR
// / OCR fit your buying power and look well-timed to enter.
//
// Region median $psf are indicative recent anchors (updatable). They convert an
// abstract price index into an affordability comparison; label them clearly as
// indicative in the UI and let the user sanity-check against real listings.

import type { SeriesSignal } from "./timing";
import type { DistrictTrend } from "../api";
import { scoreEntryFit, type EntryFit, type EntryStance } from "./entryfit";

// Optional profile-aware layer. When a stance is supplied, each recommendation
// also carries an EntryFit (blended score + decision label) and the list is
// ranked by it; without it, the original band-then-entry-score ranking stands.
export interface EntryFitOpts {
  stance: EntryStance;
  trends?: Map<number, DistrictTrend>; // district number -> longitudinal trend
}

// FALLBACK ONLY (undated, unsourced — see DESIGN.md assumptions register).
// Callers prefer the live URA resale median (series private.resale.psf.<region>)
// and use these only when that series isn't available.
export const REGION_MEDIAN_PSF: Record<string, number> = {
  ccr: 2800, // Core Central (indicative recent median $psf)
  rcr: 2250, // Rest of Central
  ocr: 1650, // Outside Central
};

export const REGION_INFO: Record<string, { label: string; districts: string }> = {
  ccr: {
    label: "CCR — Core Central",
    districts: "Districts 9, 10, 11 (Orchard, River Valley, Bukit Timah, Holland, Novena, Newton), Downtown Core, Sentosa",
  },
  rcr: {
    label: "RCR — Rest of Central",
    districts: "Fringe of the central area, e.g. Tiong Bahru, Queenstown, Alexandra, Geylang, Katong, Toa Payoh, Bishan fringe",
  },
  ocr: {
    label: "OCR — Outside Central",
    districts: "Mass-market suburbs, e.g. Bedok, Tampines, Pasir Ris, Jurong, Woodlands, Yishun, Sengkang, Punggol, Bukit Panjang",
  },
};

// Postal district -> common residential area name (for private-property granularity).
export const DISTRICT_NAMES: Record<number, string> = {
  1: "Raffles Place / Marina", 2: "Tanjong Pagar / Anson", 3: "Tiong Bahru / Queenstown",
  4: "HarbourFront / Sentosa", 5: "Buona Vista / Clementi / Pasir Panjang", 6: "City Hall / Clarke Quay",
  7: "Bugis / Beach Road", 8: "Little India / Farrer Park", 9: "Orchard / River Valley",
  10: "Bukit Timah / Holland / Tanglin", 11: "Novena / Newton / Thomson", 12: "Balestier / Toa Payoh",
  13: "Macpherson / Potong Pasir", 14: "Geylang / Eunos / Paya Lebar", 15: "Katong / Marine Parade / East Coast",
  16: "Bedok / Upper East Coast", 17: "Changi / Loyang", 18: "Tampines / Pasir Ris",
  19: "Serangoon / Hougang / Punggol / Sengkang", 20: "Ang Mo Kio / Bishan", 21: "Upper Bukit Timah / Clementi Park",
  22: "Jurong / Boon Lay", 23: "Bukit Batok / Bukit Panjang / Choa Chu Kang", 24: "Lim Chu Kang / Tengah",
  25: "Kranji / Woodlands", 26: "Upper Thomson / Springleaf", 27: "Yishun / Sembawang", 28: "Seletar / Yio Chu Kang",
};

// HDB town -> primary postal district, so town-level commute results can be
// priced with private (URA) per-district $psf. Approximate (a town can span
// districts); used only to attach a representative private median for blending.
export const TOWN_DISTRICT: Record<string, number> = {
  "ANG MO KIO": 20, "BEDOK": 16, "BISHAN": 20, "BUKIT BATOK": 23, "BUKIT MERAH": 3,
  "BUKIT PANJANG": 23, "BUKIT TIMAH": 21, "CENTRAL AREA": 1, "CHOA CHU KANG": 23,
  "CLEMENTI": 5, "GEYLANG": 14, "HOUGANG": 19, "JURONG EAST": 22, "JURONG WEST": 22,
  "KALLANG/WHAMPOA": 12, "MARINE PARADE": 15, "PASIR RIS": 18, "PUNGGOL": 19,
  "QUEENSTOWN": 3, "SEMBAWANG": 27, "SENGKANG": 19, "SERANGOON": 19, "TAMPINES": 18,
  "TOA PAYOH": 12, "WOODLANDS": 25, "YISHUN": 27,
};

export type RegionBand = "within" | "reachable" | "stretch" | "beyond";

export interface RegionRec {
  slug: string;
  label: string;
  districts: string;
  medianPsf: number;
  medianSource: "URA" | "indicative"; // real URA data vs anchor
  affordablePsf: number;
  ratio: number;
  band: RegionBand;
  entryScore: number | null;
  verdict: string | null;
  note: string;
  entryFit?: EntryFit; // set when an EntryFitOpts stance is supplied
}

function band(ratio: number): RegionBand {
  return ratio >= 1.1 ? "within" : ratio >= 1 ? "reachable" : ratio >= 0.85 ? "stretch" : "beyond";
}

/**
 * Rank CCR/RCR/OCR by fit: affordability (your affordable $psf vs region median)
 * blended with market timing (the region's price-index entry score).
 */
export function recommendPrivateRegions(
  maxPrice: number,
  areaSqft: number,
  signals: SeriesSignal[],
  opts?: EntryFitOpts,
): RegionRec[] {
  const affordablePsf = maxPrice / (areaSqft || 900);
  const byId = new Map(signals.map((s) => [s.id, s]));

  const recs: RegionRec[] = ["ccr", "rcr", "ocr"].map((slug) => {
    // Prefer real URA median $psf (once the URA key is set); else an anchor.
    const uraPsf = byId.get(`private.resale.psf.${slug}`)?.latest_value ?? null;
    const medianPsf = uraPsf && uraPsf > 0 ? uraPsf : REGION_MEDIAN_PSF[slug];
    const medianSource: "URA" | "indicative" = uraPsf && uraPsf > 0 ? "URA" : "indicative";
    const ratio = affordablePsf / medianPsf;
    const b = band(ratio);
    // Timing from the smoother quarterly price index.
    const sig = byId.get(`private.ppi.${slug}`);
    const entryScore = sig?.entry_score ?? null;
    const verdict = sig?.verdict ?? null;

    let note: string;
    if (b === "beyond") {
      note = `A ${Math.round(ratio * 100)}% budget fit — typically beyond reach here. Consider a smaller unit or another region.`;
    } else if (b === "stretch") {
      note = "A stretch at your target size — feasible with a smaller unit or a bigger downpayment.";
    } else {
      note = entryScore !== null && entryScore >= 62
        ? "Within budget and the price index looks favourable to enter — a real window."
        : entryScore !== null && entryScore <= 40
          ? "Within budget, but prices look stretched vs trend — no rush; watch for a dip."
          : "Within budget; timing is neutral — enter when a specific unit fits.";
    }
    const rec: RegionRec = { slug, label: REGION_INFO[slug].label, districts: REGION_INFO[slug].districts, medianPsf, medianSource, affordablePsf, ratio, band: b, entryScore, verdict, note };
    if (opts) {
      // Regions have no single per-district longitudinal trend, so EntryFit
      // reweights affordability + the quarterly-index timing by stance only.
      rec.entryFit = scoreEntryFit({ band: b, ratio, entryScore, trend: null, stance: opts.stance });
    }
    return rec;
  });

  if (opts) {
    recs.sort((a, b2) => (b2.entryFit?.score ?? 0) - (a.entryFit?.score ?? 0));
    return recs;
  }
  // Rank: affordable first, then better entry timing.
  const rank: Record<RegionBand, number> = { within: 0, reachable: 1, stretch: 2, beyond: 3 };
  recs.sort((a, b2) => rank[a.band] - rank[b2.band] || (b2.entryScore ?? 0) - (a.entryScore ?? 0));
  return recs;
}

export interface DistrictRec {
  district: number;
  name: string;
  medianPsf: number;
  affordablePsf: number;
  ratio: number;
  band: RegionBand;
  entryScore: number | null;
  verdict: string | null;
  entryFit?: EntryFit; // set when an EntryFitOpts stance is supplied
}

/** Rank private postal districts (from real URA per-district median $psf) by
 *  affordability at the target unit size, then market timing. When `opts` is
 *  supplied, each rec also gets a profile-aware EntryFit and the list is ranked
 *  by that instead — the affordability/timing fields above are left untouched. */
export function recommendPrivateDistricts(
  maxPrice: number,
  areaSqft: number,
  signals: SeriesSignal[],
  opts?: EntryFitOpts,
): DistrictRec[] {
  const affordablePsf = maxPrice / (areaSqft || 900);
  const out: DistrictRec[] = [];
  for (const s of signals) {
    if (s.metric !== "district_psf") continue;
    const m = /\.d(\d{2})$/.exec(s.id);
    const medianPsf = s.latest_value ?? 0;
    if (!m || medianPsf <= 0) continue;
    const district = Number(m[1]);
    const ratio = affordablePsf / medianPsf;
    const b = band(ratio);
    const rec: DistrictRec = {
      district,
      name: DISTRICT_NAMES[district] ?? `District ${district}`,
      medianPsf,
      affordablePsf,
      ratio,
      band: b,
      entryScore: s.entry_score,
      verdict: s.verdict,
    };
    if (opts) {
      const t = opts.trends?.get(district) ?? null;
      rec.entryFit = scoreEntryFit({
        band: b, ratio, entryScore: s.entry_score,
        trend: t && t.trendReady ? t : null,
        stance: opts.stance,
      });
    }
    out.push(rec);
  }
  if (opts) {
    out.sort((a, b) => (b.entryFit?.score ?? 0) - (a.entryFit?.score ?? 0));
    return out;
  }
  const rank: Record<RegionBand, number> = { within: 0, reachable: 1, stretch: 2, beyond: 3 };
  out.sort((a, b) => rank[a.band] - rank[b.band] || (b.entryScore ?? 0) - (a.entryScore ?? 0));
  return out;
}
